import * as React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Snackbar,
  Alert,
  Grid,
  CircularProgress,
  Stack,
  Divider,
  Typography,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { Html5Qrcode } from "html5-qrcode";
import { defineDevice } from "../../api/deviceAPI";
import { getCurrentTenant } from "../../config/tenantConfig";

// Flexible parser: extract { serialDevice, token } from arbitrary QR content.
// Supports JSON, key=value pairs, and delimited "serial<sep>token" strings.
export function parseQrPayload(text) {
  if (!text) return null;
  const raw = String(text).trim();
  if (!raw) return null;

  const pickSerial = (o) =>
    o.serialDevice ?? o.serial ?? o.serialNo ?? o.serialNumber ?? o.sn ??
    o.deviceSerial ?? o.serial_number ?? null;
  const pickToken = (o) =>
    o.token ?? o.key ?? o.deviceKey ?? o.deviceToken ?? o.secret ?? null;

  // 1) JSON object
  try {
    const obj = JSON.parse(raw);
    if (obj && typeof obj === "object") {
      const s = pickSerial(obj);
      const tk = pickToken(obj);
      if (s != null) {
        return {
          serialDevice: String(s).trim(),
          token: tk != null ? String(tk).trim() : "",
        };
      }
    }
  } catch (_) {
    // not JSON — continue
  }

  // 2) key=value pairs (e.g. "serialDevice=ABC;token=xyz")
  if (raw.includes("=") || /serial|token|key/i.test(raw)) {
    const map = {};
    raw.split(/[;&\n,|]+/).forEach((pair) => {
      const m = pair.split(/[=:]/);
      if (m.length >= 2) {
        map[m[0].trim().toLowerCase()] = m.slice(1).join("=").trim();
      }
    });
    const s =
      map.serialdevice || map.serial || map.serialno || map.serialnumber ||
      map.sn || map.deviceserial;
    const tk = map.token || map.key || map.devicekey || map.secret;
    if (s) return { serialDevice: s, token: tk || "" };
  }

  // 3) delimited "serial<sep>token"
  const parts = raw.split(/[;,|\t\r\n ]+/).filter(Boolean);
  if (parts.length >= 2) {
    return { serialDevice: parts[0].trim(), token: parts[1].trim() };
  }
  if (parts.length === 1) {
    return { serialDevice: parts[0].trim(), token: "" };
  }
  return null;
}

export default function DeviceControls({ onDevicesUpdate }) {
  const { t } = useTranslation();

  const [openAddDialog, setOpenAddDialog] = React.useState(false);
  const [serialDevice, setSerialDevice] = React.useState("");
  const [token, setToken] = React.useState("");
  const [isCreating, setIsCreating] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Camera / QR scanning state
  const [openCamera, setOpenCamera] = React.useState(false);
  const [cameraError, setCameraError] = React.useState("");

  const rawReaderId = React.useId();
  const readerId = `qr-reader-${rawReaderId.replace(/:/g, "")}`;
  const scannerRef = React.useRef(null);
  const handledRef = React.useRef(false);
  const onScanRef = React.useRef(() => {});

  const stopScanner = React.useCallback(async () => {
    const inst = scannerRef.current;
    if (inst) {
      try {
        if (inst.isScanning) await inst.stop();
        await inst.clear();
      } catch (_) {
        // ignore stop/clear errors
      }
      scannerRef.current = null;
    }
  }, []);

  const startScanner = React.useCallback(async () => {
    setCameraError("");
    handledRef.current = false;
    try {
      const html5Qr = new Html5Qrcode(readerId);
      scannerRef.current = html5Qr;
      await html5Qr.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => onScanRef.current(decodedText),
        () => {}, // per-frame decode failures are expected — ignore
      );
    } catch (err) {
      setCameraError(
        err?.message ||
          "Kamera başlatılamadı. İzinleri ve HTTPS bağlantısını kontrol edin.",
      );
    }
  }, [readerId]);

  // Start scanner when the camera dialog opens; stop on close/unmount.
  React.useEffect(() => {
    if (!openCamera) return undefined;
    let active = true;
    const id = setTimeout(() => {
      if (active) startScanner();
    }, 0);
    return () => {
      active = false;
      clearTimeout(id);
      stopScanner();
    };
  }, [openCamera, startScanner, stopScanner]);

  // RCU-only feature
  if (getCurrentTenant().projectCode !== "RCU") return null;

  const handleOpenAddDialog = () => setOpenAddDialog(true);

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setSerialDevice("");
    setToken("");
  };

  const handleSnackbarClose = () => setSnackbar((s) => ({ ...s, open: false }));

  // Core POST — shared by manual form submit and QR auto-submit.
  const submitDefine = async (serial, tk) => {
    if (isCreating) return;
    if (!serial || !serial.trim() || !tk || !tk.trim()) {
      setSnackbar({
        open: true,
        message:
          t("devices.dialogs.defineDevice.required") ||
          "Seri No ve Key zorunludur.",
        severity: "error",
      });
      return;
    }
    try {
      setIsCreating(true);
      await defineDevice({
        serialDevice: serial.trim(),
        token: tk.trim(),
      });
      setSnackbar({
        open: true,
        message: t("devices.messages.deviceDefined") || "Terminal tanımlandı",
        severity: "success",
      });
      handleCloseAddDialog();
      if (onDevicesUpdate) onDevicesUpdate();
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error.message ||
          t("devices.messages.errorDefiningDevice") ||
          "Terminal tanımlanamadı",
        severity: "error",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleDefineDevice = () => submitDefine(serialDevice, token);

  const handleOpenCamera = () => {
    setCameraError("");
    setOpenCamera(true);
  };
  const handleCloseCamera = () => setOpenCamera(false);

  // Latest scan handler (kept in a ref so html5-qrcode always calls fresh logic).
  onScanRef.current = async (decodedText) => {
    if (handledRef.current) return;
    const parsed = parseQrPayload(decodedText);
    if (!parsed || !parsed.serialDevice || !parsed.token) {
      setCameraError(
        t("devices.dialogs.defineDevice.invalidQr") ||
          "QR çözümlenemedi (seri no / key eksik).",
      );
      return; // keep scanning
    }
    handledRef.current = true;
    await stopScanner();
    setOpenCamera(false);
    await submitDefine(parsed.serialDevice, parsed.token);
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleOpenAddDialog}
        size="small"
        sx={{ borderRadius: 2 }}
      >
        {t("devices.controls.addTerminal") || "Terminal Ekle"}
      </Button>

      {/* Define Device Dialog */}
      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: "primary.main", color: "white", pb: 1 }}>
          {t("devices.dialogs.defineDevice.title") || "Terminal Tanımla"}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 1 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {/* Camera / QR scan button */}
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startIcon={<CameraAltIcon />}
                onClick={handleOpenCamera}
                sx={{ borderRadius: 2, py: 1.2, textTransform: "none" }}
              >
                {t("devices.dialogs.defineDevice.scanWithCamera") ||
                  "QR ile Tara"}
              </Button>
            </Grid>

            {/* Divider between camera and manual entry */}
            <Grid item xs={12}>
              <Divider>
                <Typography variant="caption" color="text.secondary">
                  {t("common.or") || "veya"}
                </Typography>
              </Divider>
            </Grid>

            <Grid item xs={12}>
              <TextField
                autoFocus
                label={t("devices.dataGrid.serialNumber") || "Seri No"}
                type="text"
                fullWidth
                variant="outlined"
                size="small"
                value={serialDevice}
                onChange={(e) => setSerialDevice(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("devices.dialogs.defineDevice.key") || "Key"}
                type="text"
                fullWidth
                variant="outlined"
                size="small"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
          <Button
            onClick={handleCloseAddDialog}
            color="primary"
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleDefineDevice}
            color="primary"
            variant="contained"
            sx={{ borderRadius: 2, minWidth: 120 }}
            disabled={isCreating}
          >
            {isCreating ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} thickness={5} color="inherit" />
                <span>{t("common.creating") || "Creating..."}</span>
              </Stack>
            ) : (
              t("common.create") || "Oluştur"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Camera / QR Scan Dialog */}
      <Dialog
        open={openCamera}
        onClose={handleCloseCamera}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {t("devices.dialogs.defineDevice.scanTitle") || "QR Kodu Tara"}
          <IconButton onClick={handleCloseCamera} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            id={readerId}
            sx={{
              width: "100%",
              "& video": { borderRadius: 1, width: "100% !important" },
            }}
          />
          {cameraError ? (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {cameraError}
            </Typography>
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1, textAlign: "center" }}
            >
              {t("devices.dialogs.defineDevice.scanHint") ||
                "QR kodu kameraya gösterin; okunduğunda terminal otomatik tanımlanır."}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseCamera}
            color="primary"
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t("common.cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === "error" ? 3000 : 6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
          elevation={6}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
